import {
  createContext,
  useState,
  useMemo,
  useCallback,
  FC,
  PropsWithChildren
} from "react";
import { CampaignStatus, LineItem } from "@madhive/mad-sdk";
import { ProcessingListItem } from "frontier/lib/components/ProcessingList";
import ProcessingPane, {
  ProcessingListContextValue
} from "frontier/lib/components/ProcessingPane";
import { downloadBlob } from "frontier/lib/utils/files";
import { madSDK } from "lib/sdk";
import { downloadCSV, Extension } from "lib/utils/csv";
import { generateErrorCsvRows } from "./utils";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "appReducers/authReducer";
import {
  LEGACY_PRODUCT_SET_LIVE_MESSAGE,
  anyLegacyLineItemBeingSetLive
} from "frontier/lib/campaigns/utils/legacyProductSetLive";
import { useToast } from "frontier/lib/hooks/stateful/useToast";
import { Intent } from "@blueprintjs/core";

type BulkEditLineItemsContextValue = ProcessingListContextValue & {
  bulkEdit: (edits: Partial<LineItem>[]) => Promise<void>;
};

export const BulkEditLineItemsContext =
  createContext<BulkEditLineItemsContextValue>({
    items: [],
    setItems: () => {},
    bulkEdit: async () => {}
  });

export const useBulkEditLineItems = () => {
  const [items, setItems] = useState<ProcessingListItem[]>([]);
  const currentUser = useSelector(selectCurrentUser);
  const addToast = useToast();

  const itemsMemoized = useMemo(() => items, [items]);

  const bulkEdit = useCallback(
    async (edits: Partial<LineItem>[]) => {
      const newBillingEnabled =
        currentUser?.settings?.newBillingEnabled === true;

      const jobId = new Date().valueOf();
      const jobName = `Bulk Edit ${edits.length} Line Items`;

      let blockedByLegacyGuard = false;
      try {
        blockedByLegacyGuard = await anyLegacyLineItemBeingSetLive(
          edits.map(e => ({
            status: e.status as CampaignStatus | undefined,
            productId: e.productId
          })),
          newBillingEnabled
        );
      } catch (e) {
        console.error("Legacy product preflight failed", e);
      }
      if (blockedByLegacyGuard) {
        addToast({
          title: "Error",
          intent: Intent.DANGER,
          message: LEGACY_PRODUCT_SET_LIVE_MESSAGE
        });
        setItems(prev => [
          ...prev,
          {
            id: jobId,
            text: `${jobName} - ${LEGACY_PRODUCT_SET_LIVE_MESSAGE}`,
            status: "error"
          }
        ]);
        return;
      }

      setItems(prev => [
        ...prev,
        {
          id: jobId,
          text: jobName,
          status: "loading"
        }
      ]);

      return madSDK.campaigns.lineItems.bulk
        .save(edits)
        .then(csv => {
          const filename = "Bulk Edit Line Items Report";
          setItems(prev => [
            ...prev.filter(job => job.id !== jobId),
            {
              id: jobId,
              text: jobName,
              status: "success"
            },
            {
              id: `${jobId}-csv`,
              text: `${filename}.csv`,
              status: "success",
              button: {
                label: "Download",
                callback: () =>
                  downloadBlob(new Blob([csv]), `${filename}.${Extension.CSV}`)
              }
            }
          ]);
        })
        .catch(error => {
          const errorCsvRows = generateErrorCsvRows(
            Array.isArray(error) ? error : [],
            edits
          );

          setItems(prev => [
            ...prev.filter(job => job.id !== jobId),
            {
              id: jobId,
              text: `${jobName} - failed`,
              status: "error",
              button: errorCsvRows.length
                ? {
                    label: "download",
                    callback: () =>
                      downloadCSV(
                        errorCsvRows,
                        ["Line Item ID", "Error"],
                        "Bulk edit errors"
                      )
                  }
                : {
                    label: "retry",
                    callback: () => {
                      setItems(previous =>
                        previous.filter(job => job.id !== jobId)
                      );
                      bulkEdit(edits);
                    }
                  }
            }
          ]);
        });
    },
    [currentUser, addToast]
  );

  return {
    items: itemsMemoized,
    setItems,
    bulkEdit
  };
};

export const BulkEditLineItemsContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const value = useBulkEditLineItems();

  return (
    <BulkEditLineItemsContext.Provider value={value}>
      {children}
      <ProcessingPane
        title="Bulk Edit upload"
        items={value.items}
        setItems={value.setItems}
      />
    </BulkEditLineItemsContext.Provider>
  );
};
